import React, { useRef, useState } from 'react';
import { Box, Button, Typography, Link, Fade } from '@mui/material';
import logo from '../images/discovery-logo.png';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import ErrorAlert from '../components/universal/alerts/ErrorAlert';
import TextInput from '../components/universal/inputs/TextInput';
import { fadeTimeout } from '../helpers/Themes';
import { useCustomTheme } from '../contexts/ThemeContext';
import SuccessAlert from '../components/universal/alerts/SuccessAlert';
import { createWorkerFactory, useWorker } from '@shopify/react-web-worker';
import {
    POST_FORGOT_PASSWORD_FAILED,
    POST_FORGOT_PASSWORD_SUCCESS,
} from '../helpers/Messages';
import ReCAPTCHA from 'react-google-recaptcha';

const validationSchema = Yup.object({
    email: Yup.string()
        .email('Enter a valid email')
        .required('Email is required'),
});

const createApiWorker = createWorkerFactory(
    () => import('../workers/ApiWorker')
);

const ForgotPasswordPage: React.FC = () => {
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const { theme } = useCustomTheme();
    const apiWorker = useWorker(createApiWorker);
    const [success, setSuccess] = React.useState<string>('');
    const [error, setError] = React.useState<string>('');
    const [showError, setShowError] = React.useState<boolean>(false);

    const [captchaValue, setCaptchaValue] = useState<string | null>(null);

    const handleCaptchaChange = (value: string | null) => {
        setCaptchaValue(value);
    };

    return (
        <Fade in={true} timeout={fadeTimeout}>
            <Box
                sx={{
                    minHeight: '100dvh',
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'center' },
                    alignItems: 'center',
                    paddingTop: { xs: 4, sm: 0 },
                    padding: 2,
                    overflowY: 'auto',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '2rem',
                        width: '100%',
                        maxWidth: '400px',
                   //     minHeight: '350px', // This ensures the form height remains constant
                    }}
                >
                    {/* Logo */}
                    <Box
                        component="img"
                        src={logo}
                        alt="Logo"
                        sx={{ width: '80px', marginBottom: '2rem' }}
                    />

                    {/* Title */}
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 700,
                            marginBottom: '0rem',
                            cursor: 'default',
                        }}
                    >
                        FORGOT PASSWORD
                    </Typography>

                    {/* Subtitle */}
                    <Typography
                        variant="body2"
                        sx={{
                            marginBottom: '2rem',
                            color: theme.palette.text.disabled,
                            cursor: 'default',
                        }}
                    >
                        please enter your email to receive an email to reset
                        your password
                    </Typography>

                    {showError && <ErrorAlert message={error} />}

                    {success !== '' && <SuccessAlert message={success} />}

                    <Formik
                        initialValues={{ email: '' }}
                        validationSchema={validationSchema}
                        onSubmit={async (values, { resetForm }) => {
                            setShowError(false);
                            setSuccess('');
                            if (captchaValue && recaptchaRef.current) {
                                try {
                                    await apiWorker.forgotPassword(
                                        values.email,
                                        captchaValue
                                    );
                                    setSuccess(POST_FORGOT_PASSWORD_SUCCESS);
                                    setCaptchaValue(null);
                                    recaptchaRef.current.reset();
                                    resetForm();
                                } catch (error) {
                                    setError(POST_FORGOT_PASSWORD_FAILED);
                                    setShowError(true);
                                    setCaptchaValue(null);
                                    recaptchaRef.current.reset();
                                }
                            }
                        }}
                    >
                        {({
                            errors,
                            touched,
                            values,
                            handleChange,
                            isSubmitting,
                            handleBlur,
                            isValid,
                        }) => (
                            <Form style={{ width: '100%' }}>
                                <TextInput
                                    id="email"
                                    label="Email"
                                    value={values.email}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    hasSubmitted={isSubmitting}
                                    touched={touched.email}
                                    errors={errors.email}
                                    enableLabel={true}
                                />

                                {/* Forgot Password Link */}
                                <Box
                                    sx={{
                                        textAlign: 'right',
                                        alignSelf: 'flex-end',
                                        marginBottom: '1rem',
                                    }}
                                >
                                    <Link
                                        href="/login"
                                        underline="hover"
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.warning.main,
                                        }}
                                    >
                                        <i>Remembered your password? </i>{' '}
                                        <i>
                                            <b>Login!</b>
                                        </i>
                                    </Link>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center', // Centers horizontally
                                        alignItems: 'center', // Centers vertically
                                        marginBottom: 2,
                                    }}
                                >
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={
                                            process.env
                                                .REACT_APP_RECAPTCHA_SITE_KEY as string
                                        }
                                        onChange={handleCaptchaChange}
                                    />
                                </Box>

                                <Button
                                    fullWidth
                                    type="submit"
                                    disabled={
                                        isSubmitting ||
                                        captchaValue === null ||
                                        !isValid
                                    }
                                    variant="contained"
                                    sx={{
                                        padding: '0.8rem',
                                        marginBottom: 1.8,
                                    }}
                                >
                                    REQUEST
                                </Button>
                            </Form>
                        )}
                    </Formik>

                    {/* Register Link */}
                    <Typography variant="body2">
                        <Link
                            href="/register"
                            underline="hover"
                            variant="body2"
                            sx={{ color: theme.palette.warning.main }}
                        >
                            <i>Not a member yet? </i>{' '}
                            <i>
                                <b>Register!</b>
                            </i>
                        </Link>
                    </Typography>
                </Box>
            </Box>
        </Fade>
    );
};

export default ForgotPasswordPage;
